import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { PortalFormDetailResponse } from "./types";

// DELETE /api/portal/forms/attachments/:id — partner removes an attachment
// from a draft. Optimistic remove (mirror bucket 3 deal-comment pattern):
// drop the row from the cached detail's draft.attachments immediately,
// rollback on error.

type Context = {
  previous: PortalFormDetailResponse | undefined;
  key: ReturnType<typeof queryKeys.portal.forms.detail>;
};

export function useRemoveFormAttachment(slug: string) {
  const queryClient = useQueryClient();
  return useMutation<void, ApiClientError, { id: string }, Context>({
    mutationFn: async ({ id }) => {
      await apiFetch<void>(`/portal/forms/attachments/${id}`, {
        method: "DELETE",
      });
    },
    onMutate: async ({ id }) => {
      const key = queryKeys.portal.forms.detail(slug);
      await queryClient.cancelQueries({ queryKey: key });
      const previous =
        queryClient.getQueryData<PortalFormDetailResponse>(key);
      if (previous?.draft) {
        queryClient.setQueryData<PortalFormDetailResponse>(key, {
          ...previous,
          draft: {
            ...previous.draft,
            attachments: previous.draft.attachments.filter(
              (a) => a.id !== id,
            ),
          },
        });
      }
      return { previous, key };
    },
    onError: (err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(context.key, context.previous);
      }
      toast.error(err.message ?? "Failed to remove attachment");
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.portal.forms.detail(slug),
      });
    },
  });
}
